import React from "react";
import styled from "styled-components";
import {
    Button,
    FormControl,
    FormLabel,
    IconButton,
    List,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    TextField,
} from "@mui/material";
import RemoveIcon from "@mui/icons-material/Remove";
import { TagsSelect } from "react-select-material-ui";
import { TextEntryFormControl } from "../TextEntryFormControl";
import { StyledFormHelperText } from "./CommonStyled";
import { IssueData } from "../../Utilities/ControlRenderContext";
import { PropertyEditorBoolean } from "../../../CommonUI/PropertyEditing/PropertyEditorBoolean";
import { StringDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/StringDataDefinition";
import { IDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { DataUtilities } from "../../../rule-engine-core/Common/DataUtilities";
import { StateManager } from "../../../rule-engine-core/Common/StateManager";
import { UIStyleConstants } from "../../../rule-engine-core/Common/UIStyleConstants";
import { UISpacedBoxContent } from "../../../CommonUI/BuildlingBlocks/UISpacedBoxContent";

class XTagGroupEditUI extends React.Component<{ editMe: TagGroupFormControl }> {
    render() {
        return (
            <UISpacedBoxContent>
                {this.props.editMe.renderBaseDataControlEditor()}

                <PropertyEditorBoolean
                    label={"Allowed To Add New Tag"}
                    propertyName={"allowedNewTag"}
                    editObject={this.props.editMe}
                />
                <List style={{ width: "100%" }}>
                    {this.props.editMe.list.map((s: string, idx: number) => (
                        <ListItem>
                            <ListItemText>
                                <TextField
                                    label="Option Label"
                                    value={s}
                                    size="small"
                                    onChange={(event) => {
                                        this.props.editMe.list[idx] = event.target.value;
                                        StateManager.changed(this.props.editMe);
                                    }}
                                    key={idx}
                                />
                            </ListItemText>
                            <ListItemSecondaryAction>
                                <IconButton
                                    color="secondary"
                                    size="small"
                                    onClick={() => {
                                        this.props.editMe.list.splice(idx, 1);
                                        StateManager.changed(this.props.editMe);
                                    }}
                                >
                                    <RemoveIcon />
                                </IconButton>
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))}
                </List>

                <Button
                    sx={{ background: UIStyleConstants.BLUE_COLOR }}
                    onClick={() => {
                        this.props.editMe.list.push("");
                        StateManager.changed(this.props.editMe);
                    }}
                >
                    Add Item
                </Button>
            </UISpacedBoxContent>
        );
    }
}

export class TagGroupFormControl extends TextEntryFormControl {
    readonly __type: string = "Tag Group";

    list: string[] = [];
    allowedNewTag?: boolean = false;

    constructor() {
        super();
        this.canFocus = false;
    }

    render() {
        let items = this.list;

        if (!items || items.length === 0) {
            items = ["Sample Tag"];
        }

        const issueData: IssueData | null = this.getFormContext()!.getControlContext(this)!.getRuntimeIssueData();
        const issueText: string = issueData?.text || "";

        return (
            <StyledFormControl error={Boolean(issueText)}>
                <FormLabel component="legend">{this.label}</FormLabel>
                <TagsSelect
                    options={items}
                    onChange={this.handleChange}
                    SelectProps={{
                        isCreatable: this.allowedNewTag ? true : false,
                        msgNoOptionsAvailable: "All tags are selected",
                        msgNoOptionsMatchFilter: "No tag matches the filter",
                    }}
                    data-testid={this.valueName}
                />
                {(issueText || this.hintText) && (
                    <StyledFormHelperText
                        error={Boolean(issueText)}
                        data-testid={`${this.valueName || "tag-group"}-hinttext`}
                    >
                        {issueText ? issueText : this.hintText}
                    </StyledFormHelperText>
                )}
            </StyledFormControl>
        );
    }

    renderEditUI(): JSX.Element | null {
        return <XTagGroupEditUI editMe={this} />;
    }

    getAddedData(): IDataDefinition | IDataDefinition[] | undefined {
        if (!this.valueName) return;

        let def = new StringDataDefinition();
        def.list = true;
        def.name = this.valueName;
        return def;
    }

    getStorageData(): object {
        let retData = super.getStorageData();

        retData = DataUtilities.addStorageData(this, retData, "__type", "list", "allowedNewTag");

        return retData;
    }

    setStorageData(data: object): void {
        super.setStorageData(data);

        DataUtilities.setStorageData(this, data, "__type", "list", "allowedNewTag");
    }

    private handleChange = (value: string[]) => {
        if (this.valueName) {
            this.setFormDataValue(this.valueName, value);
        }
    };
}

const StyledFormControl = styled(FormControl)`
    width: 100%;
`;
